import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "clearAll",
    "content",
    "emptyState",
    "fields",
    "search",
    "searchIcon",
    "selectedChips",
    "selectedCount",
    "summary",
    "trigger",
    "validation",
  ]

  static values = {
    inputName: String,
    placeholderText: String,
    searchPlaceholderText: String,
    removeLabel: String,
    summaryLimit: { type: Number, default: 1 },
    maxSelectionCount: { type: Number, default: 0 },
    overlay: { type: Boolean, default: false },
    remoteUrl: String,
    remoteMinLength: { type: Number, default: 2 },
  }

  connect() {
    this.closeOnOutsideClick = this.closeOnOutsideClick.bind(this)
    this.repositionOverlay = this.repositionOverlay.bind(this)
    window.addEventListener("click", this.closeOnOutsideClick)

    this.sync()
  }

  disconnect() {
    window.removeEventListener("click", this.closeOnOutsideClick)
    window.clearTimeout(this.remoteTimeout)
    this.remoteRequest?.abort()
    this.stopOverlayTracking()
  }

  toggle() {
    this.contentTarget.classList.contains("tw-hidden") ? this.open() : this.close()
  }

  open() {
    if (this.triggerTarget.disabled) return

    this.contentTarget.classList.remove("tw-hidden")
    this.updateOpenState()
    if (this.overlayValue) this.startOverlayTracking()

    window.setTimeout(() => this.searchTarget.focus(), 0)
  }

  close() {
    this.contentTarget.classList.add("tw-hidden")
    this.resetSearch()
    this.updateOpenState()
    this.stopOverlayTracking()
  }

  onKeydown(event) {
    if (event.key === "Escape") {
      if (!this.contentTarget.classList.contains("tw-hidden")) {
        event.preventDefault()
        this.close()
        this.triggerTarget.focus()
      }
      return
    }

    if (event.target !== this.triggerTarget) return
    if (!this.contentTarget.classList.contains("tw-hidden")) return

    if (["Enter", "ArrowDown"].includes(event.key)) {
      event.preventDefault()
      this.open()
    }
  }

  clear(event) {
    event.preventDefault()
    event.stopPropagation()

    this.clearSelection()
  }

  search() {
    if (this.contentTarget.classList.contains("tw-hidden")) this.open()

    if (this.isRemote) {
      this.scheduleRemoteSearch()
      return
    }

    this.filter()
  }

  scheduleRemoteSearch() {
    window.clearTimeout(this.remoteTimeout)
    this.remoteTimeout = window.setTimeout(() => this.performRemoteSearch(), 500)
  }

  async performRemoteSearch() {
    const term = this.searchTarget.value.trim()

    this.remoteRequest?.abort()
    if (term.length < this.remoteMinLengthValue) {
      this.renderRemoteResults([])
      return
    }

    this.remoteRequest = new AbortController()
    const { signal } = this.remoteRequest
    const url = new URL(this.remoteUrlValue, window.location.origin)
    url.searchParams.set("q", term)

    try {
      const response = await fetch(url, { headers: { Accept: "application/json" }, credentials: "same-origin", signal })
      if (!response.ok) return

      this.renderRemoteResults(this.normalizeRemoteResults(await response.json()))
    } catch (error) {
      if (signal.aborted) return

      console.warn("Remote multi-select search failed", error)
      this.renderRemoteResults([])
    }
  }

  renderRemoteResults(results) {
    const selected = this.selectedOptions.map((option) => ({
      value: option.value,
      label: option.label,
      summaryLabel: option.label,
    }))
    const selectedValues = new Set(selected.map((option) => String(option.value)))
    const fresh = results.filter((option) => !selectedValues.has(String(option.value)))

    this.replaceOptions([...selected, ...fresh], { resetSearch: false })
    this.toggleEmptyState(
      fresh.length === 0 && this.searchTarget.value.trim().length >= this.remoteMinLengthValue
    )
  }

  normalizeRemoteResults(payload) {
    return (payload?.results || []).map((result) => ({
      value: result.id,
      label: result.details ? `${result.text} (${result.details})` : result.text,
      summaryLabel: result.text,
    }))
  }

  toggleEmptyState(show) {
    if (this.hasEmptyStateTarget) this.emptyStateTarget.classList.toggle("tw-hidden", !show)
  }

  get isRemote() {
    return this.hasRemoteUrlValue && this.remoteUrlValue.length > 0
  }

  filter() {
    const normalizedSearch = this.searchTarget.value.trim().toLowerCase()

    this.optionRows.forEach((row) => {
      const matches = !normalizedSearch || row.textContent.toLowerCase().includes(normalizedSearch)

      if (matches) {
        row.style.removeProperty("display")
      } else {
        row.style.setProperty("display", "none", "important")
      }
    })
  }

  remove(event) {
    event.preventDefault()
    event.stopPropagation()

    const value = event.currentTarget.dataset.value

    this.checkboxInputs.forEach((input) => {
      if (input.value === value) {
        input.checked = false
      }
    })

    this.resetSearch()
    this.sync()
    this.validateIfInvalid()
    this.emitChange()
    this.refocusSearch()
  }

  syncOnChange(event) {
    this.enforceSelectionLimit(event)
    this.resetSearch()
    this.sync()
    this.validateIfInvalid()
    this.emitChange()
    this.refocusSearch()
  }

  refocusSearch() {
    if (this.contentTarget.classList.contains("tw-hidden")) return

    this.searchTarget.focus()
  }

  closeOnOutsideClick(event) {
    if (!this.element.contains(event.target)) this.close()
  }

  startOverlayTracking() {
    this.positionOverlay()
    window.addEventListener("scroll", this.repositionOverlay, true)
    window.addEventListener("resize", this.repositionOverlay)
  }

  stopOverlayTracking() {
    window.removeEventListener("scroll", this.repositionOverlay, true)
    window.removeEventListener("resize", this.repositionOverlay)
  }

  repositionOverlay() {
    this.positionOverlay()
  }

  positionOverlay() {
    const style = this.contentTarget.style

    if (window.matchMedia("(max-width: 1024px)").matches) {
      style.top = "50%"
      style.left = "50%"
      style.transform = "translate(-50%, -50%)"
      style.width = "calc(100vw - 24px)"
      style.maxWidth = "420px"
      return
    }

    const rect = this.triggerTarget.getBoundingClientRect()
    style.top = `${rect.bottom + 4}px`
    style.left = `${rect.left}px`
    style.width = `${rect.width}px`
    style.transform = "none"
    style.maxWidth = "none"
  }

  sync() {
    const selectedOptions = this.selectedOptions
    const selectedCount = selectedOptions.length

    this.summaryTarget.replaceChildren(...this.summaryNodes(selectedOptions))
    if (this.hasSelectedChipsTarget) {
      this.selectedChipsTarget.replaceChildren(
        ...selectedOptions.map((option) => this.renderChip(option, "selected-chip"))
      )
    }

    if (this.hasSelectedCountTarget) {
      this.selectedCountTarget.textContent = `Selected (${selectedCount})`
    }
    this.syncValidation(selectedCount)
    if (this.hasClearAllTarget) {
      this.clearAllTarget.disabled = this.triggerTarget.disabled || selectedCount === 0
    }
    this.triggerTarget.classList.toggle("filled", selectedCount > 0)
    this.updateOpenState()
  }

  updateOpenState() {
    const isOpen = !this.contentTarget.classList.contains("tw-hidden")

    this.summaryTarget.classList.toggle("tw-hidden", isOpen)
    this.element.classList.toggle("is-open", isOpen)
    this.triggerTarget.setAttribute("aria-expanded", isOpen)
    this.updateSearchAffordance(isOpen)
  }

  updateSearchAffordance(isOpen) {
    this.triggerTarget.placeholder = isOpen ? this.searchPlaceholderTextValue : " "
    this.triggerTarget.classList.toggle("tw-pl-[46px]", isOpen)

    if (this.hasSearchIconTarget) {
      this.searchIconTarget.classList.toggle("tw-hidden", !isOpen)
      this.searchIconTarget.classList.toggle("tw-flex", isOpen)
    }
  }

  summaryNodes(selectedOptions) {
    if (!selectedOptions.length) return [this.renderPlaceholder()]

    const nodes = selectedOptions
      .slice(0, this.summaryLimitValue)
      .map((option) => this.renderChip(option, "summary-chip"))

    if (selectedOptions.length > this.summaryLimitValue) {
      const node = this.cloneTemplate("summary-more-text")
      const count = selectedOptions.length - this.summaryLimitValue

      node.textContent = node.textContent.replace("%{count}", count)
      nodes.push(node)
    }

    return nodes
  }

  renderPlaceholder() {
    const node = this.cloneTemplate("placeholder")

    node.textContent = this.placeholderTextValue
    return node
  }

  renderChip(option, templateName) {
    const chip = this.cloneTemplate(templateName)
    const label = chip.querySelector('[data-multi-select-dropdown-part="label"]')
    const removeButton = chip.querySelector('[data-multi-select-dropdown-part="remove"]')

    chip.dataset.value = option.value
    label.textContent = option.label

    if (removeButton) {
      removeButton.dataset.value = option.value
      removeButton.setAttribute("aria-label", this.removeLabelValue)
    }

    return chip
  }

  cloneTemplate(name) {
    const template = this.element.querySelector(`template[data-multi-select-dropdown-template="${name}"]`)

    return template.content.firstElementChild.cloneNode(true)
  }

  resetSearch() {
    this.searchTarget.value = ""

    if (this.isRemote) {
      window.clearTimeout(this.remoteTimeout)
      this.remoteRequest?.abort()
      this.renderRemoteResults([])
      return
    }

    this.filter()
  }

  clearSelection({ emitChange = true } = {}) {
    this.checkboxInputs.forEach((input) => {
      input.checked = false
    })

    this.resetSearch()
    this.sync()
    this.validateIfInvalid()

    if (emitChange) this.emitChange()
  }

  replaceOptions(options = [], { resetSearch = true } = {}) {
    const selectedValues = new Set(this.selectedOptions.map((option) => option.value))

    this.fieldsTarget.replaceChildren(
      ...options.map((option) => this.buildOptionRow(option, selectedValues.has(String(option.value))))
    )

    const selectionChanged = this.selectionChanged(selectedValues)

    if (resetSearch) this.resetSearch()
    this.sync()
    this.validateIfInvalid()
    if (selectionChanged) this.emitChange()
  }

  syncValidation(selectedCount) {
    if (!this.hasValidationTarget) return

    this.validationTarget.value = selectedCount > 0 ? selectedCount : ""
  }

  validateIfInvalid() {
    if (!this.hasValidationTarget || !this.triggerTarget.classList.contains("is-invalid")) return

    window.materialValidator?.showError(this.validationTarget)
  }

  enforceSelectionLimit(event) {
    if (this.maxSelectionCountValue <= 0) return

    const changedInput = event.target
    if (!changedInput.checked) return

    const checkedInputs = this.checkboxInputs.filter((input) => input.checked)

    if (checkedInputs.length <= this.maxSelectionCountValue) return

    if (this.maxSelectionCountValue === 1) {
      checkedInputs.forEach((input) => {
        if (input !== changedInput) input.checked = false
      })
      return
    }

    changedInput.checked = false
  }

  get selectedOptions() {
    return this.selectedInputs
      .map((input) => ({
        label: input.dataset.multiSelectDropdownSummaryLabel,
        value: input.value,
        data: input.dataset,
      }))
  }

  get selectedInputs() {
    return this.checkboxInputs.filter((input) => input.checked)
  }

  get checkboxInputs() {
    return Array.from(this.fieldsTarget.querySelectorAll('input[type="checkbox"]'))
  }

  get optionRows() {
    return Array.from(this.fieldsTarget.querySelectorAll(".md-choise"))
  }

  buildOptionRow(option, checked) {
    const value = String(option.value)
    const label = option.label || option.summaryLabel || value
    const summaryLabel = option.summaryLabel || label
    const inputId = `${this.triggerTarget.id}_${value}`
    const row = this.cloneTemplate(option.description ? "option-row-multiline" : "option-row")
    const input = row.querySelector('[data-multi-select-dropdown-part="option-input"]')
    const text = row.querySelector('[data-multi-select-dropdown-part="option-label"]')

    row.title = summaryLabel

    if (this.inputNameValue) {
      input.name = this.inputNameValue
    } else {
      input.removeAttribute("name")
    }
    input.value = value
    input.id = inputId
    input.checked = checked
    input.defaultChecked = checked
    input.disabled = this.triggerTarget.disabled
    input.dataset.multiSelectDropdownSummaryLabel = summaryLabel

    if (checked) {
      input.setAttribute("checked", "checked")
    } else {
      input.removeAttribute("checked")
    }

    if (option.description) {
      text.querySelector('[data-multi-select-dropdown-part="option-title"]').textContent = label
      text.querySelector('[data-multi-select-dropdown-part="option-description"]').textContent = option.description
    } else {
      text.textContent = label
    }

    return row
  }

  selectionChanged(previousSelectedValues) {
    const nextSelectedValues = new Set(this.selectedOptions.map((option) => option.value))

    if (previousSelectedValues.size !== nextSelectedValues.size) return true

    return Array.from(previousSelectedValues).some((value) => !nextSelectedValues.has(value))
  }

  emitChange() {
    this.element.dispatchEvent(new CustomEvent("multi-select-dropdown:change", {
      bubbles: true,
      detail: { selectedOptions: this.selectedOptions }
    }))
  }
}
